import { create } from 'zustand';

import { openDispatchLog } from './review-cards-store';

/**
 * Plan 2026-10-15-004 §6.5 — the Dispatch log's view toggle and its "Only this
 * person" filter, lifted out of `DispatchInspector` so the member panel's "All
 * of Ana's sends" link can set them from a different part of the Review step.
 *
 * Pure view state (nothing here is seeded from server data, so no
 * `useDraftState`). The Type and Status chips stay local to the table: nothing
 * outside it sets them. `PlayFlowShell` calls {@link clearDispatchLogFilters}
 * with the other play-scoped stores so one Play's person filter never narrows
 * the next Play's log.
 */
/** The Dispatch log card's DOM id — "All of Ana's sends" scrolls to it. */
export const DISPATCH_LOG_ANCHOR_ID = 'playbooks-dispatch-log';

export type DispatchLogView = 'people' | 'sends';

interface DispatchLogFilterState {
  view: DispatchLogView;
  /** The campaign member the "All people" table is narrowed to, or `null` for everyone. */
  personMemberId: string | null;
  /** The name the "Ana Park ×" chip shows. */
  personLabel: string | null;
}

/** Exported per the store convention — reset idiom is `useDispatchLogFilterStore.setState(initialState, true)`. */
export const initialState: DispatchLogFilterState = {
  view: 'people',
  personMemberId: null,
  personLabel: null,
};

export const useDispatchLogFilterStore = create<DispatchLogFilterState>(() => ({ ...initialState }));

export function setDispatchLogView(view: DispatchLogView): void {
  useDispatchLogFilterStore.setState({ view });
}

/**
 * Narrows the log to one person — and shows the table, the only view that can
 * be narrowed. Expands the Dispatch log too (it starts collapsed), so a link
 * that sends the rep to it never lands on a closed card.
 */
export function showOnlyPerson(memberId: string, label: string): void {
  openDispatchLog();
  useDispatchLogFilterStore.setState({ view: 'people', personMemberId: memberId, personLabel: label });
}

/** Removes the person filter (the chip's ×). */
export function clearPersonFilter(): void {
  useDispatchLogFilterStore.setState({ personMemberId: null, personLabel: null });
}

/** The play-scoped reset `PlayFlowShell` performs. */
export function clearDispatchLogFilters(): void {
  useDispatchLogFilterStore.setState({ ...initialState });
}
